import { getJwtString } from '@backoffice-frontend/authentication'
import { ENV, Environment } from '@backoffice-frontend/environment'
import {
  Features,
  useIsFeatureToggleEnabled,
} from '@backoffice-frontend/feature-toggle'
import cube from '@cubejs-client/core'
import { CubeProvider as Provider } from '@cubejs-client/react'
import type { ReactNode } from 'react'

// API endpoints
const backofficeDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: 'http://localhost:4000/cubejs-api/v1',
  [Environment.test]: '',
  [Environment.development]:
    'https://backoffice-data-api.insights.dev.moia-group.io/cubejs-api/v1',
  [Environment.integration]:
    'https://backoffice-data-api.insights.int.moia-group.io/cubejs-api/v1',
  [Environment.production]:
    'https://backoffice-data-api.insights.prd.moia-group.io/cubejs-api/v1',
  [Environment['production.us-east-1']]:
    'https://backoffice-data-api.insights.prd.moia-group.io/cubejs-api/v1',
}
const backofficeMultitenantDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: 'http://localhost:4000/multi-tenant-api/v1',
  [Environment.test]: '',
  [Environment.development]:
    'https://multi-tenant-cube-data-api.insights.dev.moia-group.io/multi-tenant-api/v1',
  [Environment.integration]:
    'https://multi-tenant-cube-data-api.insights.int.moia-group.io/multi-tenant-api/v1',
  [Environment.production]:
    'https://multi-tenant-cube-data-api.insights.prd.moia-group.io/multi-tenant-api/v1',
  [Environment['production.us-east-1']]:
    'https://multi-tenant-cube-data-api.insights.prd.moia-group.io/multi-tenant-api/v1',
}
const dapDataApiEndpoints: Record<Environment, string> = {
  [Environment.local]: 'http://localhost:4000/cubejs-api/v1',
  [Environment.test]: '',
  [Environment.development]:
    'https://cube-api.dev.eu-central-1.api.moia-group.io/cubejs-api/v1',
  [Environment.integration]:
    'https://cube-api.int.eu-central-1.api.moia-group.io/cubejs-api/v1',
  [Environment.production]:
    'https://cube-api.prd.eu-central-1.api.moia-group.io/cubejs-api/v1',
  [Environment['production.us-east-1']]:
    'https://cube-api.prd.us-east-1.api.moia-group.io/cubejs-api/v1',
}

const getToken = async () => {
  const token = await getJwtString()
  return token ?? ''
}

// Create the cube API instances
const regularCubeApi = cube(getToken, {
  apiUrl: backofficeDataApiEndpoints[ENV],
})
const multitenantCubeApi = cube(getToken, {
  apiUrl: backofficeMultitenantDataApiEndpoints[ENV],
})
const dapCubeApi = cube(getToken, {
  apiUrl: dapDataApiEndpoints[ENV],
})

// Simple provider that allows switching between APIs
export const CubeProvider = ({
  children,
  useMultitenantCubeApi = false,
}: {
  children: ReactNode
  useMultitenantCubeApi?: boolean
}) => {
  const useDapEndpoint = useIsFeatureToggleEnabled(
    Features.DAP_CUBE_ENDPOINT_V2,
  )

  const selectedApi = useDapEndpoint
    ? dapCubeApi
    : useMultitenantCubeApi
      ? multitenantCubeApi
      : regularCubeApi

  return <Provider cubeApi={selectedApi}>{children}</Provider>
}
